import { ProjectRenderer } from '../ProjectRenderer';
import { VertexList } from '../utils/VertexList';
import { RoomVertex, SerializedPlaygroundVertex } from '../objects/RoomVertex';
import { CameraType, RoomDragType } from '@/types/creator';
import { RoomWall } from '../objects/RoomWall';
import { UUID } from '@/types/types';
import { RoomFloor } from '../objects/RoomFloor';
import { Vector2, Vector2Tuple } from 'three';
import { RendererMode } from '../types';
import { Feature, Polygon } from 'geojson';
import { polygon } from '@turf/turf';
import { RenderUtils } from '../utils/RenderUtils';
import { LandscapeModule } from '../objects/LandscapeModule';
import APP_CONFIG from '@/configs/appConfig';

export type SerializedRoomManager = {
	roomHeight: number;
	vertices: SerializedPlaygroundVertex[];
};

export class RoomManager {
	private walls: RoomWall[] = [];
	private _wallsVisible = true;
	private _roomHeight: number = APP_CONFIG.ROOM_HEIGHT.DEFAULT / 100;
	private roomDragMainVertex: RoomVertex | null = null;
	private shouldResetModulesHistory = false;
	roomDragType = RoomDragType.WALL;
	vertexList = new VertexList();
	floor: RoomFloor;

	constructor(private renderer: ProjectRenderer) {
		this.floor = new RoomFloor(this.renderer, this);
	}

	get roomHeight() {
		return this._roomHeight;
	}

	set roomHeight(height: number) {
		this._roomHeight = height;
		this.updateRoom(true);
	}

	getWall(id: UUID) {
		const wall = this.walls.find((w) => w.id === id) || null;
		return wall;
	}

	getWallByIndex(index: number) {
		return this.walls[index] ?? null;
	}

	getWallIndex(wall: RoomWall) {
		return this.walls.indexOf(wall);
	}

	private addWall(vert1: RoomVertex, vert2: RoomVertex) {
		const playgroundLine = new RoomWall(this.renderer, this, vert1, vert2);
		this.walls.push(playgroundLine);
	}

	private createWalls() {
		this.vertexList.forEach((vertex) => {
			if (!vertex.next) return;

			this.addWall(vertex, vertex.next);
		});
	}

	private setWallsLabelsAvailability(force = false) {
		const visible =
				force ||
				([
					RendererMode.ROOM,
					RendererMode.MODULES,
					RendererMode.PREVIEW,
				].includes(this.renderer.mode) &&
					this.renderer.cameraController.activeCamera ===
						CameraType.ORTHOGRAPHIC),
			isReadonly = this.renderer.mode === RendererMode.PREVIEW;

		for (const wall of this.walls) {
			wall.css2dObject.visible = visible;
			wall.input.readOnly = isReadonly;
		}
	}

	get wallsVisible() {
		return this._wallsVisible;
	}

	set wallsVisible(visible: boolean) {
		this._wallsVisible = visible;
		this.renderer.cameraController.setLayersVisibility();
		this.renderer.mouse.setAvailability();
	}

	setFromShape(vertices: Vector2Tuple[]) {
		this.dispose();
		this.renderer.drawingManager.dispose();

		for (const position of vertices) {
			const roomVertex = new RoomVertex(this.renderer, position);
			this.vertexList.add(roomVertex);
		}

		this.vertexList.closeLoop();
		this.createWalls();
		this.floor.createFloor();

		this.renderer.modulesManager.historyManager.reset();
		this.renderer.setAvailability();
	}

	updateRoom(updateVertexGeometry = false) {
		for (const wall of this.walls) {
			wall.compute();
		}

		for (const vertex of this.vertexList.toArray()) {
			vertex.compute(updateVertexGeometry);
		}

		this.floor.createFloor();
	}

	unHoverAll() {
		for (const wall of this.walls) {
			wall.isMouseOver = false;
		}
	}

	dispose() {
		this.floor.dispose();

		for (const vertex of this.vertexList.toArray()) {
			this.vertexList.remove(vertex);
			vertex.dispose();
		}

		for (const wall of this.walls) {
			wall.dispose();
		}

		this.walls = [];
	}

	setAvailability() {
		this.setWallsLabelsAvailability();
	}

	prepareScreenshot(isFinished = false) {
		this.setWallsLabelsAvailability(!isFinished);
	}

	handleWallDrag(wall: RoomWall, delta: Vector2) {
		const isVertical = wall.start.position.x === wall.end.position.x;

		if (isVertical) delta.y = 0;
		else delta.x = 0;

		const oldStartPosition = wall.start.position.clone(),
			oldEndPosition = wall.end.position.clone();

		wall.start.position = wall.start.position.clone().add(delta);
		wall.end.position = wall.end.position.clone().add(delta);

		this.updateRoom();

		const hasAnyWallIncorrectLength = this.walls.some(
			(w) => w.length < RoomWall.MIN_LENGTH || w.length > RoomWall.MAX_LENGTH
		);

		if (hasAnyWallIncorrectLength) {
			wall.start.position = oldStartPosition;
			wall.end.position = oldEndPosition;

			this.updateRoom();
			return false;
		}

		const hostedModules = this.renderer.modulesManager.getLandscapeModulesForWall(
			wall
		);
		wall.dragHostedLandscapeModules(delta);
		if (hostedModules.length) this.shouldResetModulesHistory = true;

		return true;
	}

	handleRoomDrag(delta: Vector2) {
		this.vertexList.forEach((vertex) => {
			vertex.position = vertex.position.clone().add(delta);
		});

		for (const wall of this.walls) {
			const hostedModules =
				this.renderer.modulesManager.getLandscapeModulesForWall(wall);
			if (hostedModules.length) this.shouldResetModulesHistory = true;
			wall.dragHostedLandscapeModules(delta);
		}

		this.updateRoom();
	}

	handleRoomDragEnd() {
		this.roomDragMainVertex = null;
		this.flushModulesHistoryReset();
	}

	flushModulesHistoryReset() {
		if (!this.shouldResetModulesHistory) return;
		this.shouldResetModulesHistory = false;
		this.renderer.modulesManager.historyManager.reset();
	}

	getLargestAvailableRectangle() {
		const verticiesArr = this.vertexList.toArray();

		const positions: Vector2Tuple[] = verticiesArr.map((vertex) =>
			vertex.position.toArray()
		);
		positions.push(positions[0]);

		const polygonFeature: Feature<Polygon> = polygon([positions]);

		return RenderUtils.findLargestApproximateAABB(polygonFeature);
	}

	getIntersectedWalls(landscapeModule: LandscapeModule) {
		const walls = [];

		for (const wall of this.walls) {
			const intersects = landscapeModule.box.intersectsBox(wall.box);
			if (intersects) walls.push(wall);
		}

		return walls;
	}

	export(): SerializedRoomManager {
		return {
			roomHeight: this.roomHeight,
			vertices: this.vertexList.toArray().map((v) => v.export()),
		};
	}

	loadFromData(data: SerializedRoomManager) {
		this.dispose();
		this.roomHeight = data.roomHeight;

		for (const config of data.vertices) {
			const roomVertex = new RoomVertex(this.renderer, config.position);
			this.vertexList.add(roomVertex);
		}

		this.vertexList.closeLoop();
		this.createWalls();
		this.floor.createFloor();
		this.setAvailability();
	}
}
